import moment from 'moment'

const DEFAULT_LOCALE = 'en'

/**
 * Moves a timestamp in utc to time in current locale.
 * e.g. Makes 12:00 UTC => 12:00 EST
 * @param dateAsEpoch
 * @returns {*|moment.Moment}
 */
export const getCurrentUTCDateTime = (locale = DEFAULT_LOCALE) => moment()
    .locale(locale)
    .format('YYYY-MM-DDTHH:mm:ss.SSSZ')

export const getUTCOffset = () => moment()
    .utcOffset()
    .toString()

export function getLocalDate(dateObj) {
  return new Date(dateObj.getTime() + dateObj.getTimezoneOffset() * 60000)
}

export const addUTCOffset = momentObj =>
  momentObj.add(momentObj.utcOffset(), 'minutes')

export const fromUTCMidnightSeconds = (
  dateAsEpoch = moment().valueOf() / 1000
) => moment((dateAsEpoch - moment().utcOffset() * 60) * 1000)

export const fromUTCMidnightSecondsTS = UTCTimestamp => fromUTCMidnightSeconds(moment(UTCTimestamp).valueOf() / 1000)

/**
 * Moves a timestamp object back to midnight of current day and then applies utc-offset to represent it as midnight at
 * utc timezone.
 * e.q. Makes 10:00 PM EST -> 12:00 AM UTC
 * @param dateAsMoment
 * @returns {number}
 */
export const toUTCMidnightSeconds = dateAsMoment =>
  dateAsMoment.startOf('day').valueOf() / 1000 + moment().utcOffset() * 60

/**
 * Returns when the current day started in UTC
 * @returns {moment.Moment}
 */
export const currentDayStartInUTC = () => fromUTCMidnightSeconds()

export const dayDifference = (date1, date2) => {
  const d1 = moment(date1).startOf('day')
  const d2 = moment(date2).startOf('day')
  return d2.diff(d1, 'days')
}

export const yearDifference = (date1, date2) => {
  const d1 = moment(date1).startOf('day')
  const d2 = moment(date2).startOf('day')
  return d2.diff(d1, 'years')
}

export const generateDateString = (date, strings) => {
  const momentDate = moment(date)
  const now = moment()
  const today = now.clone().startOf('day')
  const yesterday = now
    .clone()
    .subtract(1, 'days')
    .startOf('day')

  if (momentDate.isSame(today, 'day')) return momentDate.format('LT')
  if (momentDate.isSame(yesterday, 'day')) return strings?.yesterday || ''
  if (momentDate.isSame(new Date(), 'week')) return momentDate.format('dddd')
  return momentDate.format('LL')
}

export const getMomentTime = time => {
  const startIndex = time.indexOf('T') + 1
  const endIndex = startIndex + 5
  const dateString = time.slice(startIndex, endIndex)
  return moment(dateString, 'HH:mm')
}

export const getUnixTimestampSeconds = date =>
  Math.round(moment(date, 'YYYY-MM-DDTHH:mm:ss').valueOf() / 1000)

export function timeNow() {
  return new Date().toISOString()
}

export const isFutureDate = (dateString, today = new Date()) => {
  // Parse the date string into a Date object
  const dateObj = getLocalDate(new Date(dateString))
  // Extract the year, month, and day
  const year = dateObj.getFullYear()
  const month = dateObj.getMonth() + 1 // Months are 0-indexed
  const day = dateObj.getDate()

  // Create a Date object for today
  const todayYear = today.getFullYear()
  const todayMonth = today.getMonth() + 1
  const todayDay = today.getDate()

  // Compare the dates
  if (year > todayYear) {
    return true
  } if (year === todayYear) {
    if (month > todayMonth) {
      return true
    } if (month === todayMonth) {
      return day >= todayDay
    }
  }

  return false
}

/**
 * 'be' before or equal; 'b' before; 'e' equal; 'l' later; 'le' later or equal
 * @param {String} date1
 * @param {String} date2
 * @param {String} key
 */
export const compareDates = (date1, date2, key = 'be') => {
  const localDateObj1 = getLocalDate(new Date(date1))
  const localDateObj2 = getLocalDate(new Date(date2))

  // Extract the year, month, and day
  const year1 = localDateObj1.getFullYear()
  const month1 = localDateObj1.getMonth() + 1 // Months are 0-indexed
  const day1 = localDateObj1.getDate()

  const year2 = localDateObj2.getFullYear()
  const month2 = localDateObj2.getMonth() + 1 // Months are 0-indexed
  const day2 = localDateObj2.getDate()

  switch (key) {
    case 'be':
      if (year1 < year2) {
        return true
      } if (year1 === year2) {
        if (month1 < month2) {
          return true
        } if (month1 === month2) {
          return day1 <= day2
        }
      }
      break
    case 'b':
      if (year1 < year2) {
        return true
      } if (year1 === year2) {
        if (month1 < month2) {
          return true
        } if (month1 === month2) {
          return day1 < day2
        }
      }
      break
    case 'e':
      return year1 === year2 && month1 === month2 && day1 === day2
    case 'l':
      if (year1 > year2) {
        return true
      } if (year1 === year2) {
        if (month1 > month2) {
          return true
        } if (month1 === month2) {
          return day1 > day2
        }
      }
      break
    case 'le':
      if (year1 > year2) {
        return true
      } if (year1 === year2) {
        if (month1 > month2) {
          return true
        } if (month1 === month2) {
          return day1 >= day2
        }
      }
      break
    default:
      break
  }
  return false
}

export const isSameDate = (dateString, dayToCompare) => {
  // Parse the date string into a Date object
  const dateObj = new Date(dateString)
  // We add the timezone offset in milliseconds to the timestamp to get the correct local time
  const localDate = getLocalDate(dateObj)

  // Extract the year, month, and day
  const year = localDate.getFullYear()
  const month = localDate.getMonth() + 1 // Months are 0-indexed
  const day = localDate.getDate()

  // Create a Date object for today
  const dayToCompareDate = new Date(dayToCompare)
  const newDateLocale = getLocalDate(dayToCompareDate)
  const compareYear = newDateLocale.getFullYear()
  const compareMonth = newDateLocale.getMonth() + 1
  const compareDay = newDateLocale.getDate()

  return year === compareYear && month === compareMonth && day === compareDay
}
